import * as React from 'react';

import { MATHLETE_CONFIG } from '@/features/user-notifications/utils/constants';
import { analytics } from '@/lib/analytics';
import { useNotifications } from '@/stores/notifications';
import { Drawer } from '@/ui/drawer';

import { useMathleteBatches } from '../../hooks/use-mathlete-batches';
import { useRegisterMathleteBatch } from '../../hooks/use-register-mathlete-batch';
import type { MathleteBatch } from '../../types';

import { MathleteBatchSelectionView } from './mathlete-batch-selection-view';
import { MathleteSuccessView } from './mathlete-success-view';

interface MathleteRegistrationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  courseId?: string;
}

export const MathleteRegistrationDrawer: React.FC<
  MathleteRegistrationDrawerProps
> = ({ isOpen, onClose, courseId }) => {
  const [selectedBatch, setSelectedBatch] =
    React.useState<MathleteBatch | null>(null);
  const [isEnrollButtonVisible, setIsEnrollButtonVisible] =
    React.useState<boolean>(false);
  const [showSuccessView, setShowSuccessView] = React.useState<boolean>(false);
  const enrollButtonRef = React.useRef<HTMLButtonElement>(null);

  const addNotification = useNotifications(state => state.addNotification);

  const [firstCourse] = MATHLETE_CONFIG.courses;
  const defaultCourseId: string = firstCourse
    ? String(firstCourse.courseId)
    : '';
  const activeCourseId: string = courseId ?? defaultCourseId;

  const {
    batches,
    isLoading,
    isError: isBatchesError,
  } = useMathleteBatches(activeCourseId);

  const {
    registerForBatch,
    isRegistering,
    isSuccess,
    isError: isRegistrationError,
    error: registrationError,
  } = useRegisterMathleteBatch(activeCourseId);

  const handleBatchClick = (batch: MathleteBatch) => {
    setSelectedBatch(batch);

    analytics.trackEvent('MathletePopupClickedSelectBatch', 1, {
      clickType: 'batch-card',
      clickCTA: 'Select batch',
      clickSection: 'Mathlete Registration',
    });
  };

  const isBatchSelected = (batch: MathleteBatch) => {
    return selectedBatch?.batch_id === batch.batch_id;
  };

  const handleEnrollSubmit = () => {
    if (selectedBatch) {
      analytics.trackEvent('MathletePopupClickedEnrollNow', 1, {
        clickType: 'cta',
        clickCTA: 'Enroll now',
        clickSection: 'Mathlete Registration',
      });

      void registerForBatch(selectedBatch.batch_id);
    }
  };

  React.useEffect(() => {
    if (selectedBatch && !isEnrollButtonVisible) {
      setIsEnrollButtonVisible(true);
      enrollButtonRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [selectedBatch, isEnrollButtonVisible]);

  React.useEffect(() => {
    if (isSuccess) {
      setShowSuccessView(true);
      addNotification({
        title: 'Enrolled to Mathlete 101 Successfully!',
        type: 'success',
      });
    }
  }, [isSuccess, addNotification]);

  React.useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    if (showSuccessView) {
      // Close drawer after 3 seconds
      // Query invalidation in the mutation hook will refresh the data
      timer = setTimeout(() => {
        onClose();
      }, 3000);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [showSuccessView, onClose]);

  React.useEffect(() => {
    if (isOpen) {
      analytics.trackEvent('MathletePopupOpened', 1, {});
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (isSuccess && selectedBatch) {
      analytics.trackEvent('MathletePopupEnrollmentSuccess', 1, {
        batchId: selectedBatch.batch_id,
      });
    }
  }, [isSuccess, selectedBatch]);

  React.useEffect(() => {
    if (isRegistrationError && registrationError) {
      addNotification({
        title: 'Failed to enroll to Mathlete 101',
        message: registrationError.message ?? 'Please try again later',
        type: 'error',
      });
      analytics.trackEvent('MathletePopupEnrollmentError', 1, {
        errorMessage: registrationError.message ?? 'Unknown registration error',
      });
    }
  }, [isRegistrationError, registrationError, addNotification]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      contentClassName="bg-magical-moonlight-white"
      childClass="!max-w-full"
      topOffset="top-[25%]"
      closeButtonPosition={{ top: 'top-[calc(25%-45px)]', right: 'right-6' }}
      innerContentClassName="p-2 md:p-3"
      role="dialog"
      aria-modal="true"
    >
      {showSuccessView ? (
        <MathleteSuccessView selectedBatch={selectedBatch} />
      ) : (
        <MathleteBatchSelectionView
          batches={batches}
          selectedBatch={selectedBatch}
          isLoading={isLoading}
          isBatchesError={isBatchesError}
          isRegistering={isRegistering}
          enrollButtonRef={enrollButtonRef}
          onBatchClick={handleBatchClick}
          onEnrollSubmit={handleEnrollSubmit}
          isBatchSelected={isBatchSelected}
        />
      )}
    </Drawer>
  );
};
